import { ActionKind } from '@dynamic/ui';
import { t } from 'i18next';
import { FC, FocusEventHandler, useState } from 'react';
import { useParams } from 'react-router';
import styled from 'styled-components';

import { LinkStyleProps, MenuButton } from '#/components/Header/components';
import { useActions, useNavigation } from '#/hooks';
import { ButtonVariant } from '#/service/constants';
import { remify } from '#/service/utils';
import { useAuthentication } from '#/store/auth';

import { focusShadow } from '../common';
import { SettingsRouteParams, useSettingsMenus } from './useSettingsMenus';

export interface SettingsMenuLinkProps {
  menuButtonProps: Omit<LinkStyleProps, 'theme'>;
}

export const SettingsMenuLink: FC<SettingsMenuLinkProps> = ({
  children,
  menuButtonProps,
}) => {
  const { go } = useNavigation('SettingsMenuLink');
  const { settingId } = useParams<SettingsRouteParams>();
  const { settingsToUse } = useSettingsMenus();
  const [visible, setVisible] = useState(false);
  const { handleAction, actions } = useActions();
  const { isLoggedIn, isAuthEnabled } = useAuthentication();
  const isHighlighted = !!settingId;

  const handleItemClick = (key: string, action?: object) => {
    setVisible(false);

    if (action) {
      handleAction(action);

      return;
    }
    go(`/settings/${key}`);
  };

  const handleAuthentication = () => {
    setVisible(false);

    if (isLoggedIn) {
      actions[ActionKind.Logout]();
    } else {
      actions[ActionKind.Login]();
    }
  };

  // TODO: Refactor this approach according to https://github.com/Accedo-Products/one-client-web/pull/879#discussion_r871206282
  const handleBlur: FocusEventHandler<HTMLSpanElement> = (e) => {
    const currentTarget = e.currentTarget;

    // Check the newly focused element in the next tick of the event loop
    setTimeout(() => {
      // Check if the new activeElement is a child of the original container
      if (!currentTarget?.contains(document.activeElement)) {
        setVisible(false);
      }
    }, 0);
  };

  return (
    <span onBlur={handleBlur}>
      <MenuButton
        {...menuButtonProps}
        $buttonVariant={ButtonVariant.Ghost}
        aria-expanded={visible}
        aria-haspopup="true"
        aria-label={t('settings.appSettings')}
        className={isHighlighted ? 'active' : ''}
        id="settings-menu-trigger"
        onClick={() => setVisible(!visible)}
        type="button"
      >
        {children}
      </MenuButton>
      <AbsoluteMenu
        $isVisible={visible}
        aria-labelledby="settings-menu-trigger"
        id="settings-menu"
        role="menu"
      >
        {settingsToUse?.map(({ key = '', title = '', action }) => (
          <AbsoluteMenuItem
            key={key}
            onClick={() => handleItemClick(key, action)}
            role="menuitem"
            type="button"
          >
            {t(title)}
          </AbsoluteMenuItem>
        ))}
        {isAuthEnabled && (
          <AbsoluteMenuItem
            onClick={handleAuthentication}
            role="menuitem"
            type="button"
          >
            {t(isLoggedIn ? 'button.logout' : 'button.login')}
          </AbsoluteMenuItem>
        )}
      </AbsoluteMenu>
    </span>
  );
};

SettingsMenuLink.displayName = 'SettingsMenuLink';

interface AbsoluteMenuProps {
  $isVisible: boolean;
}

const AbsoluteMenu = styled.div<AbsoluteMenuProps>`
  ${({ $isVisible }) => ($isVisible ? '' : 'display: none;')}
  background-color: var(--pageSecondaryBackground);
  min-width: ${remify(200)};
  max-width: ${remify(400)};
  position: absolute;
  top: 5rem;
  padding: 0.2rem 0.5rem;
  box-shadow: 0 ${remify(3)} ${remify(9)} var(--shadowColor);

  right: 2rem;
  @media (max-width: 1200px) {
    margin-left: 1rem;
    right: 1rem;
  }
`;

AbsoluteMenu.displayName = 'AbsoluteMenu';

const AbsoluteMenuItem = styled.button`
  opacity: 1;
  cursor: pointer;
  border: none;
  background: transparent;
  text-align: left;
  display: block;
  width: 100%;
  appearance: none;
  color: var(--pageParagraphForeground);
  margin: 0.4rem 0;
  font: 0.888889rem / 1.5 ParagraphFont;
  border-radius: 1px;
  // override RightSide's padding
  && {
    padding: ${remify(8)} ${remify(10)};
  }
  &:hover {
    opacity: 0.7;
  }
  &:focus-visible {
    ${focusShadow}
  }
`;

AbsoluteMenuItem.displayName = 'AbsoluteMenuItem';
